/**
 * Track auto-router (F-series "Generate Route" feature).
 *
 * Given an OHT vehicle and a list of stockers on an otherwise-empty stretch
 * of canvas, generates brand-new overhead-rail / overhead-curve / cross-rail
 * LayoutElements that connect the OHT to every stocker, the same way a user
 * would lay track out by hand: a Manhattan (horizontal-then-vertical) tree
 * rooted at the OHT, with collinear trunk runs shared instead of duplicated
 * and a circular junction inserted wherever a branch peels off a shared trunk.
 *
 * Pure/testable: no Redux dependency. Obstacle avoidance against unrelated
 * pre-existing equipment/track is out of scope for v1.
 */
import { LayoutElement, ElementType, Dimensions } from '../types';
import { TOOLBOX_ITEMS, DEFAULT_TRACK_THICKNESS_METERS, DEFAULT_CURVE_RADIUS_METERS } from '../constants';
import { getRailEndpoints, getAABB, Pt, AABB, EQUIPMENT_TYPES, getStockerTrayPosition } from './topology';
import { layoutMetersToWorldUnits, worldUnitsToLayoutMeters } from './measurement';
import { generateId } from './geometry';
import { createEntityIdAllocator } from './entityIds';
import {
  buildOrthogonalTrackPath,
  ChainSegmentGeometry,
} from './trackChain';

const EPS = 1e-6;
const MIN_SEGMENT_LENGTH = 0.5;
const DEDUPE_RADIUS = 1;

/** Base curve radius from the shared curve-radius default. */
const BASE_CURVE_RADIUS = layoutMetersToWorldUnits(DEFAULT_CURVE_RADIUS_METERS);

/** Circular-junction footprint — diameter = 2 × curve radius (center→spoke = R). */
const JUNCTION_DIMENSIONS: Dimensions = {
  width: BASE_CURVE_RADIUS * 2,
  height: BASE_CURVE_RADIUS * 2,
};

/** Base rail thickness from the shared track-thickness default. */
const BASE_RAIL_THICKNESS = layoutMetersToWorldUnits(DEFAULT_TRACK_THICKNESS_METERS);

const toolboxDefaults = (id: string) => {
  const item = TOOLBOX_ITEMS.find((i) => i.id === id);
  return { sku: item?.sku, defaultProperties: item?.defaultProperties ?? {} };
};

const dist = (a: Pt, b: Pt): number => Math.hypot(a.x - b.x, a.y - b.y);

const rotatePt = (deg: number, x: number, y: number): Pt => {
  const r = (deg * Math.PI) / 180;
  const c = Math.cos(r);
  const s = Math.sin(r);
  return { x: x * c - y * s, y: x * s + y * c };
};

export interface AutoRouteSummary {
  railCount: number;
  curveCount: number;
  junctionCount: number;
  totalLengthWorldUnits: number;
  skippedStockerIds: string[];
  message?: string;
}

export interface AutoRouteResult {
  elements: LayoutElement[];
  summary: AutoRouteSummary;
}

const anchorOf = (el: LayoutElement): Pt => ({
  x: el.position.x + el.dimensions.width / 2,
  y: el.position.y + el.dimensions.height / 2,
});

// ─── Element builders ──────────────────────────────────────────────────────

interface BuildCtx {
  layerId: string;
  groupId: string;
  elements: LayoutElement[];
  totalLength: number;
  railN: number;
  curveN: number;
  junctionN: number;
  nextId: (type: ElementType, railType?: string | null) => string;
}

const baseMetadata = (name: string, color?: string) => ({
  name,
  locked: false,
  visible: true,
  color,
});

/** Adds a straight rail spanning two axis-aligned world points, or a no-op if too short. */
function addStraightRail(ctx: BuildCtx, a: Pt, b: Pt): void {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const horizontal = Math.abs(dy) <= Math.abs(dx) * 1e-3 + EPS;
  const vertical = Math.abs(dx) <= Math.abs(dy) * 1e-3 + EPS;
  // Spur / auto-route straights must stay on-axis. Non-aligned pairs are skipped
  // so callers can place a proper curve180 instead of inventing 90° elbows.
  if (!horizontal && !vertical) return;
  const length = horizontal ? Math.abs(dx) : Math.abs(dy);
  if (length < MIN_SEGMENT_LENGTH) return;

  const thickness = BASE_RAIL_THICKNESS;
  const { sku, defaultProperties } = toolboxDefaults('rail-at1');

  ctx.railN += 1;
  const position = horizontal
    ? { x: Math.min(a.x, b.x), y: a.y - thickness / 2 }
    : { x: a.x + thickness / 2, y: Math.min(a.y, b.y) };

  ctx.elements.push({
    id: ctx.nextId('overhead-rail', 'straight'),
    layerId: ctx.layerId,
    type: 'overhead-rail' as ElementType,
    position,
    rotation: horizontal ? 0 : 90,
    dimensions: { width: length, height: thickness },
    metadata: baseMetadata(`Auto Rail ${ctx.railN}`),
    customProperties: {
      ...defaultProperties,
      sku,
      railType: 'straight',
      lengthMeters: worldUnitsToLayoutMeters(length),
      thicknessMeters: worldUnitsToLayoutMeters(thickness),
      autoRouted: true,
      autoRouteGroupId: ctx.groupId,
    },
  });
  ctx.totalLength += length;
}

function addBuiltCurve90(ctx: BuildCtx, geometry: ChainSegmentGeometry): void {
  const radius = geometry.dimensions.width;
  const { sku, defaultProperties } = toolboxDefaults('rail-at2');
  ctx.curveN += 1;
  ctx.elements.push({
    id: ctx.nextId('overhead-curve', 'curve90'),
    layerId: ctx.layerId,
    type: 'overhead-curve' as ElementType,
    position: geometry.position,
    rotation: geometry.rotation,
    dimensions: geometry.dimensions,
    metadata: baseMetadata(`Auto Curve ${ctx.curveN}`),
    customProperties: {
      ...defaultProperties,
      sku,
      railType: 'curve90',
      radiusMeters: DEFAULT_CURVE_RADIUS_METERS,
      autoRouted: true,
      autoRouteGroupId: ctx.groupId,
    },
  });
  ctx.totalLength += (Math.PI / 2) * radius;
}

/**
 * Append an axis-aligned route through the central manufactured-track builder.
 * It returns false without mutating the context when any configured-radius
 * Curve90 cannot fit.
 */
function addOrthogonalPath(ctx: BuildCtx, vertices: Pt[]): boolean {
  const result = buildOrthogonalTrackPath(
    vertices,
    {
      lengthUnits: 1,
      thicknessUnits: BASE_RAIL_THICKNESS,
      radiusUnits: BASE_CURVE_RADIUS,
    },
    { minStraightUnits: MIN_SEGMENT_LENGTH }
  );
  if (!result.ok) return false;

  for (const piece of result.pieces) {
    if (piece.railType === 'straight') {
      const first = { x: piece.points[0], y: piece.points[1] };
      const last = {
        x: piece.points[piece.points.length - 2],
        y: piece.points[piece.points.length - 1],
      };
      addStraightRail(ctx, first, last);
    } else {
      addBuiltCurve90(ctx, piece);
    }
  }
  return true;
}

/**
 * Solves position/rotation for a circular junction whose evenly-spaced
 * spokes best-fit `legs` (world points the ports should face, in the same
 * order the caller wants them back). Ports are evenly spaced around the ring
 * (360 / maxPorts apart) — callers building a through+through+branch split
 * should pass `maxPorts: 4` so the through pair lands on two opposite ports
 * (180° apart) and the branch on a third (90° from both), leaving the 4th
 * port unused, rather than forcing all of them into 120°-apart thirds.
 *
 * Defaults to centering on the first two legs' midpoint (the through pair,
 * when there is one) rather than the centroid of every leg — a lone branch
 * leg would otherwise pull a simple average off the true through-line
 * center. Pass `center` explicitly for a hub with 3+ legs that don't form a
 * single opposing pair (e.g. several trunks radiating from one point).
 */
function solveCircleJunction(
  legs: Pt[],
  dims: Dimensions,
  maxPorts: number = Math.max(legs.length, 2),
  center?: Pt
): { position: Pt; rotation: number; maxPorts: number; legOrder: number[] } {
  const cx = center ? center.x : legs.length >= 2 ? (legs[0].x + legs[1].x) / 2 : legs[0].x;
  const cy = center ? center.y : legs.length >= 2 ? (legs[0].y + legs[1].y) / 2 : legs[0].y;

  // Prefer axis-aligned rotations first so through/branch ports land on
  // Manhattan track headings (0/90/180/270) before trying finer 15° steps.
  const candidateRotations = [
    0,
    90,
    180,
    270,
    ...Array.from({ length: 24 }, (_, i) => i * 15).filter((r) => r % 90 !== 0),
  ];
  let best: { position: Pt; rotation: number; err: number; legOrder: number[] } | null = null;

  const isCardinal = (r: number) => Math.abs(r % 90) < 1e-6;

  for (const rotation of candidateRotations) {
    // Rotation pivots around `position` (the shape's top-left corner in its
    // own local frame), not its visual center — so the position that puts
    // the world center at (cx, cy) has to be re-derived for every rotation,
    // the same way solveCurve90 solves position per rotation.
    const centerOffset = rotatePt(rotation, dims.width / 2, dims.height / 2);
    const position = { x: cx - centerOffset.x, y: cy - centerOffset.y };
    const probe: LayoutElement = {
      id: '__probe__',
      layerId: 'layer-1',
      type: 'cross-rail',
      position,
      rotation,
      dimensions: dims,
      metadata: { name: '', locked: false, visible: true },
      customProperties: { railType: 'junction-circle', maxPorts },
    };
    const spokeTips = getRailEndpoints(probe);

    // Greedily match each leg to its nearest still-unused spoke (angular fit
    // matters far more than absolute distance here — the spoke ring's radius
    // is tiny compared to how far away a real leg target is).
    const used = new Set<number>();
    const legOrder: number[] = [];
    let err = 0;
    for (const leg of legs) {
      let bestIdx = -1;
      let bestDist = Infinity;
      spokeTips.forEach((tip, idx) => {
        if (used.has(idx)) return;
        const angleToLeg = Math.atan2(leg.y - cy, leg.x - cx);
        const angleToTip = Math.atan2(tip.y - cy, tip.x - cx);
        let angleDiff = Math.abs(angleToLeg - angleToTip);
        if (angleDiff > Math.PI) angleDiff = 2 * Math.PI - angleDiff;
        if (angleDiff < bestDist) {
          bestDist = angleDiff;
          bestIdx = idx;
        }
      });
      used.add(bestIdx);
      legOrder.push(bestIdx);
      err += bestDist;
    }
    const better =
      !best ||
      err < best.err - 1e-6 ||
      (Math.abs(err - best.err) <= 1e-6 && isCardinal(rotation) && !isCardinal(best.rotation));
    if (better) best = { position, rotation, err, legOrder };
  }

  return { position: best!.position, rotation: best!.rotation, maxPorts, legOrder: best!.legOrder };
}

/**
 * Adds a junction-circle with one evenly-spaced port per leg, returning the
 * actual world ports in the SAME order as the input `legs` (unlike
 * getRailEndpoints, which returns spokes in the piece's own angle order).
 *
 * Unlike a curve, a junction-circle has no implicit through-route
 * between its ports — `buildTrackGraph` only connects pairs explicitly
 * listed in `customProperties.connections`. Every pair of used ports is
 * wired up both ways here so the piece is actually traversable end-to-end.
 */
function addCircleJunction(
  ctx: BuildCtx,
  legs: Pt[],
  dims: Dimensions = JUNCTION_DIMENSIONS,
  maxPorts?: number,
  center?: Pt
): Pt[] {
  const { position, rotation, maxPorts: resolvedMaxPorts, legOrder } = solveCircleJunction(
    legs,
    dims,
    maxPorts,
    center
  );
  const { sku, defaultProperties } = toolboxDefaults('rail-circle-junction');
  ctx.junctionN += 1;

  const connections: { id: string; fromPortId: string; toPortId: string }[] = [];
  for (let i = 0; i < legOrder.length; i++) {
    for (let j = 0; j < legOrder.length; j++) {
      if (i === j) continue;
      connections.push({
        id: `${ctx.groupId}-conn-${ctx.junctionN}-${i}-${j}`,
        fromPortId: `port-${legOrder[i]}`,
        toPortId: `port-${legOrder[j]}`,
      });
    }
  }

  const element: LayoutElement = {
    id: ctx.nextId('cross-rail', 'junction-circle'),
    layerId: ctx.layerId,
    type: 'cross-rail' as ElementType,
    position,
    rotation,
    dimensions: dims,
    metadata: baseMetadata(`Auto Circle Junction ${ctx.junctionN}`),
    customProperties: {
      ...defaultProperties,
      sku,
      railType: 'junction-circle',
      maxPorts: resolvedMaxPorts,
      connections,
      autoRouted: true,
      autoRouteGroupId: ctx.groupId,
    },
  };
  ctx.elements.push(element);
  const spokeTips = getRailEndpoints(element);
  return legOrder.map((spokeIdx) => spokeTips[spokeIdx]); // ports in `legs` order
}

/**
 * Builds a Manhattan connection between two arbitrary world points: a single
 * straight rail if they're already axis-aligned, otherwise straight + curve90
 * + straight via whichever L-corner isn't blocked by an obstacle box. This is
 * the same single-bend construction `buildFromHub` uses for a route's final
 * leg, generalized to connect any two points rather than a hub to a target.
 */
function addManhattanEdge(ctx: BuildCtx, a: Pt, aBox: AABB, b: Pt, bBox: AABB, otherBoxes: AABB[]): boolean {
  const dx = b.x - a.x;
  const dy = b.y - a.y;

  if (Math.abs(dy) < EPS || Math.abs(dx) < EPS) {
    return addOrthogonalPath(ctx, [a, b]);
  }

  const cornerHFirst = { x: b.x, y: a.y };
  const cornerVFirst = { x: a.x, y: b.y };
  const blockedBy = (p: Pt) =>
    pointInAABB(p, aBox) || pointInAABB(p, bBox) || otherBoxes.some((box) => pointInAABB(p, box));
  const useHFirst = !blockedBy(cornerHFirst) || blockedBy(cornerVFirst);
  const corner = useHFirst ? cornerHFirst : cornerVFirst;

  return addOrthogonalPath(ctx, [a, corner, b]);
}

/**
 * Direction (and available run-length) of the final leg where an
 * addManhattanEdge-style route from `a` arrives at `b` — mirrors its corner
 * logic exactly. The length matters because a caller offsetting backward
 * from `b` (e.g. to make room for a flanking junction) must not shift past
 * this leg's own corner: overshooting it changes `addManhattanEdge`'s own
 * independent corner choice for the shifted target, silently flipping the
 * direction it actually builds relative to what was assumed here.
 */
function manhattanApproach(
  a: Pt,
  aBox: AABB,
  b: Pt,
  bBox: AABB,
  otherBoxes: AABB[]
): { direction: Pt; legLength: number } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (Math.abs(dy) < EPS || Math.abs(dx) < EPS) {
    const len = Math.hypot(dx, dy) || 1;
    return { direction: { x: dx / len, y: dy / len }, legLength: Math.hypot(dx, dy) };
  }
  const cornerHFirst = { x: b.x, y: a.y };
  const cornerVFirst = { x: a.x, y: b.y };
  const blockedBy = (p: Pt) =>
    pointInAABB(p, aBox) || pointInAABB(p, bBox) || otherBoxes.some((box) => pointInAABB(p, box));
  const useHFirst = !blockedBy(cornerHFirst) || blockedBy(cornerVFirst);
  return useHFirst
    ? { direction: { x: 0, y: dy >= 0 ? 1 : -1 }, legLength: Math.abs(dy) }
    : { direction: { x: dx >= 0 ? 1 : -1, y: 0 }, legLength: Math.abs(dx) };
}

const pointBox = (p: Pt): AABB => ({ minX: p.x, minY: p.y, maxX: p.x, maxY: p.y });

/** Solves position/rotation for a curve180 U-turn connecting entry `e1` to exit `e2`.
 * When `bulgeToward` is set, the semicircle MUST bow that direction (outward),
 * never back toward the through-line / junctions.
 */
function solveCurve180(
  e1: Pt,
  e2: Pt,
  radius: number,
  bulgeToward?: Pt
): { position: Pt; rotation: number; sweepSign: 1 | -1 } {
  const mid = { x: (e1.x + e2.x) / 2, y: (e1.y + e2.y) / 2 };
  const toE1 = { x: e1.x - mid.x, y: e1.y - mid.y };
  const rawDeg = (Math.atan2(toE1.y, toE1.x) * 180) / Math.PI;
  const rotations = [0, 90, 180, 270];
  let best: {
    position: Pt;
    rotation: number;
    sweepSign: 1 | -1;
    err: number;
    outwardDot: number;
  } | null = null;

  const preferred = rotations.reduce((a, b) =>
    Math.abs(((b - rawDeg + 540) % 360) - 180) < Math.abs(((a - rawDeg + 540) % 360) - 180) ? b : a
  );

  const bulgeLen = bulgeToward ? Math.hypot(bulgeToward.x, bulgeToward.y) : 0;
  const bulgeDir =
    bulgeToward && bulgeLen > EPS
      ? { x: bulgeToward.x / bulgeLen, y: bulgeToward.y / bulgeLen }
      : null;

  for (const rotation of [preferred, ...rotations.filter((r) => r !== preferred)]) {
    const offset = rotatePt(rotation, radius, 0);
    const positioned = { x: e1.x - offset.x, y: e1.y - offset.y };

    for (const sweepSign of [1, -1] as const) {
      const probe: LayoutElement = {
        id: '__probe__',
        layerId: 'layer-1',
        type: 'overhead-curve',
        position: positioned,
        rotation,
        dimensions: { width: radius, height: radius },
        metadata: { name: '', locked: false, visible: true },
        customProperties: { railType: 'curve180', curveSweepSign: sweepSign },
      };
      const [p1, p2] = getRailEndpoints(probe);
      const err = dist(p1, e1) + dist(p2, e2);

      // Apex at signed 90° — direction from diameter mid through apex is the bulge.
      const apexOffset = rotatePt(rotation, 0, sweepSign * radius);
      const apex = { x: positioned.x + apexOffset.x, y: positioned.y + apexOffset.y };
      const outwardDot = bulgeDir
        ? (apex.x - mid.x) * bulgeDir.x + (apex.y - mid.y) * bulgeDir.y
        : 0;

      const better =
        !best ||
        err < best.err - 1e-6 ||
        (Math.abs(err - best.err) <= 1e-6 && outwardDot > best.outwardDot);
      if (better) best = { position: positioned, rotation, sweepSign, err, outwardDot };
    }
  }

  // Hard guarantee: if the best endpoint fit still bulges inward, flip the sweep.
  if (best && bulgeDir && best.outwardDot < 0) {
    best = {
      ...best,
      sweepSign: best.sweepSign === 1 ? -1 : 1,
      outwardDot: -best.outwardDot,
    };
  }

  return {
    position: best!.position,
    rotation: best!.rotation,
    sweepSign: best!.sweepSign,
  };
}

/** Adds a curve180 U-turn connecting entry `e1` to exit `e2`; returns actual endpoints. */
function addCurve180(ctx: BuildCtx, e1: Pt, e2: Pt, radius: number, bulgeToward?: Pt): [Pt, Pt] {
  const { position, rotation, sweepSign } = solveCurve180(e1, e2, radius, bulgeToward);
  const { sku, defaultProperties } = toolboxDefaults('rail-at2');
  ctx.curveN += 1;
  const element: LayoutElement = {
    id: ctx.nextId('overhead-curve', 'curve180'),
    layerId: ctx.layerId,
    type: 'overhead-curve' as ElementType,
    position,
    rotation,
    dimensions: { width: radius, height: radius },
    metadata: baseMetadata(`Auto Curve ${ctx.curveN}`),
    customProperties: {
      ...defaultProperties,
      sku,
      railType: 'curve180',
      curveSweepSign: sweepSign,
      radiusMeters: worldUnitsToLayoutMeters(radius),
      autoRouted: true,
      autoRouteGroupId: ctx.groupId,
    },
  };
  ctx.elements.push(element);
  ctx.totalLength += Math.PI * radius;
  const [p1, p2] = getRailEndpoints(element);
  return [p1, p2];
}

/**
 * Places a circular junction whose two through ports sit at throughA/throughB
 * and whose branch port points toward `perp` from their midpoint; returns
 * just the branch port (for attaching a dock spur).
 */
function addSpurJunction(ctx: BuildCtx, throughA: Pt, throughB: Pt, perp: Pt, half: number): Pt {
  const mid = { x: (throughA.x + throughB.x) / 2, y: (throughA.y + throughB.y) / 2 };
  const branchTarget = { x: mid.x + perp.x * half, y: mid.y + perp.y * half };
  const [, , branchPort] = addCircleJunction(ctx, [throughA, throughB, branchTarget], JUNCTION_DIMENSIONS, 4);
  return branchPort;
}

/** Distance from a loop stop to each flanking spur circular junction's outer through port. */
const SPUR_JUNCTION_SPAN = JUNCTION_DIMENSIONS.width;
/** How far the dock lane reaches out from its circular junction toward the stocker. */
const SPUR_DOCK_DEPTH = 40;

// ─── Routing ───────────────────────────────────────────────────────────────

type Axis = 'h' | 'v';

interface StockerRoute {
  target: Pt;
  axis: Axis; // first-leg axis from the OHT
  sign: 1 | -1; // first-leg direction sign along that axis
  kind: 'straight' | 'bend';
  perpSign?: 1 | -1; // direction of the second leg, for 'bend'
}

const pointInAABB = (p: Pt, box: { minX: number; minY: number; maxX: number; maxY: number }) =>
  p.x > box.minX && p.x < box.maxX && p.y > box.minY && p.y < box.maxY;

function planStockerRoute(
  ohtAnchor: Pt,
  ohtBox: ReturnType<typeof getAABB>,
  stocker: LayoutElement,
  otherBoxes: ReturnType<typeof getAABB>[]
): StockerRoute {
  const target = getStockerTrayPosition(stocker);
  const dx = target.x - ohtAnchor.x;
  const dy = target.y - ohtAnchor.y;
  const stockerBox = getAABB(stocker);

  if (Math.abs(dy) < EPS) {
    return { target, axis: 'h', sign: dx >= 0 ? 1 : -1, kind: 'straight' };
  }
  if (Math.abs(dx) < EPS) {
    return { target, axis: 'v', sign: dy >= 0 ? 1 : -1, kind: 'straight' };
  }

  const cornerHFirst = { x: target.x, y: ohtAnchor.y };
  const cornerVFirst = { x: ohtAnchor.x, y: target.y };
  const blockedBy = (p: Pt) =>
    pointInAABB(p, ohtBox) || pointInAABB(p, stockerBox) || otherBoxes.some((b) => pointInAABB(p, b));

  const useHFirst = !blockedBy(cornerHFirst) || blockedBy(cornerVFirst);

  return {
    target,
    axis: useHFirst ? 'h' : 'v',
    sign: useHFirst ? (dx >= 0 ? 1 : -1) : (dy >= 0 ? 1 : -1),
    kind: 'bend',
    perpSign: useHFirst ? (dy >= 0 ? 1 : -1) : (dx >= 0 ? 1 : -1),
  };
}

/** Extent of a route's first leg along its axis, from the OHT anchor. */
function extentOf(route: StockerRoute, ohtAnchor: Pt): number {
  return route.axis === 'h'
    ? Math.abs(route.target.x - ohtAnchor.x)
    : Math.abs(route.target.y - ohtAnchor.y);
}

function pointAlongAxis(ohtAnchor: Pt, axis: Axis, sign: 1 | -1, extent: number): Pt {
  return axis === 'h'
    ? { x: ohtAnchor.x + sign * extent, y: ohtAnchor.y }
    : { x: ohtAnchor.x, y: ohtAnchor.y + sign * extent };
}

/**
 * Builds a Manhattan trunk radiating from `hub` along `axis`/`sign`, merging
 * every route that shares a collinear run and inserting a circular junction
 * wherever a branch peels off before the trunk's end. Routes whose corner coincides
 * exactly with another route's (a tie) are not nudged apart — nudging would
 * de-align the corner from the stocker's true perpendicular coordinate and
 * break the leg — instead they're resolved by recursing on the perpendicular
 * axis from that shared point, exactly like a second, smaller hub.
 */
function buildFromHub(ctx: BuildCtx, hub: Pt, axis: Axis, sign: 1 | -1, routes: StockerRoute[]): boolean {
  const withExtent = routes.map((route) => ({ route, extent: extentOf(route, hub) }));

  const clusters: { extent: number; routes: StockerRoute[] }[] = [];
  withExtent
    .sort((a, b) => a.extent - b.extent)
    .forEach(({ route, extent }) => {
      const last = clusters[clusters.length - 1];
      if (last && Math.abs(last.extent - extent) < EPS) {
        last.routes.push(route);
      } else {
        clusters.push({ extent, routes: [route] });
      }
    });

  let prevPoint = hub;
  let prevExtent = 0;
  let valid = true;

  clusters.forEach((cluster, idx) => {
    if (!valid) return;
    const isLastCluster = idx === clusters.length - 1;
    const currentPoint = pointAlongAxis(hub, axis, sign, cluster.extent);

    const bendRoutes = cluster.routes.filter((r) => r.kind === 'bend');

    // Any 'straight' route in this cluster sits exactly at currentPoint, and a
    // tied cluster of 2+ bends recurses from currentPoint as a secondary hub —
    // both need the trunk plainly extended there first. A single bend instead
    // builds its own partial trunk-filler segment (to e1/throughA) below.
    if (bendRoutes.length !== 1) {
      addStraightRail(ctx, prevPoint, currentPoint);
      prevPoint = currentPoint;
      prevExtent = cluster.extent;
    }

    const runAvailable = cluster.extent - prevExtent;

    if (bendRoutes.length === 1) {
      const route = bendRoutes[0];
      const perpAxis: Axis = axis === 'h' ? 'v' : 'h';
      const perpSign = route.perpSign as 1 | -1;

      if (isLastCluster) {
        valid = addOrthogonalPath(ctx, [prevPoint, currentPoint, route.target]);
      } else {
        const half = JUNCTION_DIMENSIONS.width / 2;
        const clampedHalf = Math.max(1, Math.min(half, runAvailable / 2 - MIN_SEGMENT_LENGTH));
        const throughA =
          axis === 'h'
            ? { x: currentPoint.x - sign * clampedHalf, y: currentPoint.y }
            : { x: currentPoint.x, y: currentPoint.y - sign * clampedHalf };
        const throughB =
          axis === 'h'
            ? { x: currentPoint.x + sign * clampedHalf, y: currentPoint.y }
            : { x: currentPoint.x, y: currentPoint.y + sign * clampedHalf };
        const branch =
          perpAxis === 'v'
            ? { x: currentPoint.x, y: currentPoint.y + perpSign * clampedHalf }
            : { x: currentPoint.x + perpSign * clampedHalf, y: currentPoint.y };

        const junctionDims: Dimensions = { width: clampedHalf * 2, height: clampedHalf * 2 };
        addStraightRail(ctx, prevPoint, throughA);
        const [, , branchPort] = addCircleJunction(ctx, [throughA, throughB, branch], junctionDims, 4);
        addStraightRail(ctx, branchPort, route.target);
        prevPoint = throughB;
        prevExtent = cluster.extent;
        return;
      }
    } else if (bendRoutes.length > 1) {
      // Tie: 2+ branches peel off at the exact same point. Treat currentPoint
      // as a secondary hub and recurse per perpendicular direction — each
      // sub-group merges/branches further exactly like a top-level trunk.
      const perpAxis: Axis = axis === 'h' ? 'v' : 'h';
      const bySign = new Map<1 | -1, StockerRoute[]>();
      bendRoutes.forEach((route) => {
        const s = route.perpSign as 1 | -1;
        if (!bySign.has(s)) bySign.set(s, []);
        bySign.get(s)!.push(route);
      });
      bySign.forEach((group, perpSign) => {
        if (!valid) return;
        const pseudoRoutes: StockerRoute[] = group.map((route) => ({
          target: route.target,
          axis: perpAxis,
          sign: perpSign,
          kind: 'straight',
        }));
        valid = buildFromHub(ctx, currentPoint, perpAxis, perpSign, pseudoRoutes);
      });
    }

    prevPoint = currentPoint;
    prevExtent = cluster.extent;
  });
  return valid;
}

/** 'tree' (default) dead-ends a branch at each stocker; 'loop' visits every
 *  stocker in a single closed Manhattan ring with no dead ends. */
export type RouteMode = 'tree' | 'loop';

/**
 * Generates a Manhattan-tree track network connecting an OHT vehicle to every
 * given stocker. Pure function — returns new elements to create, does not
 * dispatch anything.
 */
export function generateAutoRoute(
  oht: LayoutElement,
  stockers: LayoutElement[],
  existingElements: LayoutElement[] = [],
  options?: { groupId?: string; mode?: RouteMode }
): AutoRouteResult {
  if (options?.mode === 'loop') {
    return generateLoopRoute(oht, stockers, existingElements, options?.groupId ?? generateId());
  }

  const groupId = options?.groupId ?? generateId();
  const alloc = createEntityIdAllocator(existingElements.map((el) => el.id));
  const ctx: BuildCtx = {
    layerId: oht.layerId,
    groupId,
    elements: [],
    totalLength: 0,
    railN: 0,
    curveN: 0,
    junctionN: 0,
    nextId: (type, railType) => alloc.nextForElement(type, railType),
  };

  const skippedStockerIds: string[] = [];

  if (stockers.length === 0) {
    return {
      elements: [],
      summary: {
        railCount: 0,
        curveCount: 0,
        junctionCount: 0,
        totalLengthWorldUnits: 0,
        skippedStockerIds,
        message: 'No stockers to connect.',
      },
    };
  }

  const ohtAnchor = anchorOf(oht);
  const ohtBox = getAABB(oht);

  // Dedupe stockers by anchor point, and drop any sitting exactly on the OHT.
  const seenAnchors: Pt[] = [];
  const uniqueStockers: LayoutElement[] = [];
  stockers.forEach((stocker) => {
    const a = anchorOf(stocker);
    if (dist(a, ohtAnchor) < DEDUPE_RADIUS) {
      skippedStockerIds.push(stocker.id);
      return;
    }
    if (seenAnchors.some((s) => dist(s, a) < DEDUPE_RADIUS)) {
      skippedStockerIds.push(stocker.id);
      return;
    }
    seenAnchors.push(a);
    uniqueStockers.push(stocker);
  });

  if (uniqueStockers.length === 0) {
    return {
      elements: [],
      summary: {
        railCount: 0,
        curveCount: 0,
        junctionCount: 0,
        totalLengthWorldUnits: 0,
        skippedStockerIds,
        message: 'No reachable stockers (all coincide with the OHT or each other).',
      },
    };
  }

  const equipmentBoxes = existingElements
    .filter((e) => EQUIPMENT_TYPES.has(e.type) && e.id !== oht.id)
    .map((e) => getAABB(e));

  const routes = uniqueStockers.map((stocker) =>
    planStockerRoute(ohtAnchor, ohtBox, stocker, equipmentBoxes)
  );

  const groups = new Map<string, StockerRoute[]>();
  routes.forEach((route) => {
    const key = `${route.axis}:${route.sign}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(route);
  });

  const distinctAxes = new Set(Array.from(groups.keys()).map((key) => key.split(':')[0]));
  let routeValid = true;

  if (distinctAxes.size <= 1) {
    // Every trunk shares one axis (opposite signs at most) — that's just a
    // straight pass-through the hub, not a real multi-rotation meeting, so
    // plain touching rails are correct (no connector needed).
    groups.forEach((groupRoutes, key) => {
      if (!routeValid) return;
      const [axis, signStr] = key.split(':');
      routeValid = buildFromHub(
        ctx,
        ohtAnchor,
        axis as Axis,
        Number(signStr) as 1 | -1,
        groupRoutes
      );
    });
  } else {
    // Multiple trunks radiate from the OHT in genuinely different rotation
    // angles — meet them at a circular junction instead of letting each
    // buildFromHub call start its own independent straight rail from the
    // bare OHT anchor, which would just cross the others with no connector.
    const groupKeys = Array.from(groups.keys());
    const hubHalf = JUNCTION_DIMENSIONS.width / 2;
    const legs = groupKeys.map((key) => {
      const [axis, signStr] = key.split(':');
      const sign = Number(signStr) as 1 | -1;
      return axis === 'h'
        ? { x: ohtAnchor.x + sign * hubHalf, y: ohtAnchor.y }
        : { x: ohtAnchor.x, y: ohtAnchor.y + sign * hubHalf };
    });
    const ports = addCircleJunction(ctx, legs, JUNCTION_DIMENSIONS, Math.max(4, legs.length), ohtAnchor);
    groupKeys.forEach((key, idx) => {
      if (!routeValid) return;
      const [axis, signStr] = key.split(':');
      routeValid = buildFromHub(
        ctx,
        ports[idx],
        axis as Axis,
        Number(signStr) as 1 | -1,
        groups.get(key)!
      );
    });
  }

  if (!routeValid) {
    return {
      elements: [],
      summary: {
        railCount: 0,
        curveCount: 0,
        junctionCount: 0,
        totalLengthWorldUnits: 0,
        skippedStockerIds,
        message: 'Route rejected: the configured Curve90 radius does not fit.',
      },
    };
  }

  return {
    elements: ctx.elements,
    summary: {
      railCount: ctx.railN,
      curveCount: ctx.curveN,
      junctionCount: ctx.junctionN,
      totalLengthWorldUnits: Math.round(ctx.totalLength * 100) / 100,
      skippedStockerIds,
    },
  };
}

interface RingStop {
  ids: string[];
  anchor: Pt;
  box: AABB;
}

/**
 * Generates a single closed Manhattan loop visiting the OHT and every
 * stocker, in nearest-neighbor order. Every stop (the OHT excluded) is
 * flanked by two circular junctions with a dock spur + curve180 U-turn in
 * between, so the OHT — being unidirectional — never has to back out of a
 * dead end to reach the next stop. Stockers whose anchors coincide (within
 * DEDUPE_RADIUS) share one ring stop and get this same treatment together.
 */
function generateLoopRoute(
  oht: LayoutElement,
  stockers: LayoutElement[],
  existingElements: LayoutElement[],
  groupId: string
): AutoRouteResult {
  const alloc = createEntityIdAllocator(existingElements.map((el) => el.id));
  const ctx: BuildCtx = {
    layerId: oht.layerId,
    groupId,
    elements: [],
    totalLength: 0,
    railN: 0,
    curveN: 0,
    junctionN: 0,
    nextId: (type, railType) => alloc.nextForElement(type, railType),
  };

  const skippedStockerIds: string[] = [];
  const ohtAnchor = anchorOf(oht);

  const emptyResult = (message: string): AutoRouteResult => ({
    elements: [],
    summary: {
      railCount: 0,
      curveCount: 0,
      junctionCount: 0,
      totalLengthWorldUnits: 0,
      skippedStockerIds,
      message,
    },
  });

  // Drop stockers sitting exactly on the OHT — nothing to loop through there.
  const candidates = stockers.filter((stocker) => {
    if (dist(anchorOf(stocker), ohtAnchor) < DEDUPE_RADIUS) {
      skippedStockerIds.push(stocker.id);
      return false;
    }
    return true;
  });

  if (candidates.length === 0) {
    return emptyResult('No reachable stockers (all coincide with the OHT).');
  }
  if (candidates.length < 2) {
    return emptyResult('Add at least 2 stockers to form a loop.');
  }

  // Cluster stockers that coincide with each other into one shared ring stop.
  const stops: RingStop[] = [];
  candidates.forEach((stocker) => {
    const a = anchorOf(stocker);
    const existing = stops.find((stop) => dist(stop.anchor, a) < DEDUPE_RADIUS);
    if (existing) {
      existing.ids.push(stocker.id);
    } else {
      stops.push({ ids: [stocker.id], anchor: a, box: getAABB(stocker) });
    }
  });

  // Nearest-neighbor tour starting from the OHT.
  const unvisited = [...stops];
  const order: RingStop[] = [];
  let cursor = ohtAnchor;
  while (unvisited.length > 0) {
    let bestIdx = 0;
    let bestDist = Infinity;
    unvisited.forEach((stop, idx) => {
      const d = dist(cursor, stop.anchor);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = idx;
      }
    });
    const [next] = unvisited.splice(bestIdx, 1);
    order.push(next);
    cursor = next.anchor;
  }

  const routedIds = new Set(candidates.map((s) => s.id));
  const equipmentBoxes = existingElements
    .filter((e) => EQUIPMENT_TYPES.has(e.type) && e.id !== oht.id && !routedIds.has(e.id))
    .map((e) => getAABB(e));

  // nodePoints/nodeBoxes describe where the MAIN line actually runs. Index 0
  // is the OHT (never given a dock spur, since it's a vehicle, not equipment
  // needing to dock). Every stop — a single stocker or several merged
  // coincident ones — gets flanked by two circular junctions with a dock
  // spur + curve180 U-turn between them, so the main line passes it by
  // rather than dead-ending into it: the OHT is unidirectional and can't
  // back out the way it came, so the return lane has to be a physically
  // different path.
  const nodePoints: Pt[] = [ohtAnchor, ...order.map((s) => s.anchor)];
  const nodeBoxes: AABB[] = [getAABB(oht), ...order.map((s) => s.box)];
  const half = JUNCTION_DIMENSIONS.width / 2;

  for (let i = 0; i < nodePoints.length; i++) {
    const a = nodePoints[i];
    const aBox = nodeBoxes[i];
    const nextIdx = (i + 1) % nodePoints.length;
    const stop = nodePoints[nextIdx];
    const stopBox = nodeBoxes[nextIdx];

    if (nextIdx === 0) {
      if (!addManhattanEdge(ctx, a, aBox, stop, stopBox, equipmentBoxes)) {
        return emptyResult('Route rejected: the configured Curve90 radius does not fit.');
      }
      continue;
    }

    const { direction: dirIn, legLength } = manhattanApproach(a, aBox, stop, stopBox, equipmentBoxes);
    const perp = { x: -dirIn.y, y: dirIn.x };
    // j1 must stay between the incoming edge's bend corner and `stop` —
    // shifting past the corner would make addManhattanEdge(a, j1) pick its
    // own corner independently, which can come out the opposite direction
    // from `dirIn` above (most visible when the perpendicular leg into
    // `stop` is much shorter than a junction span).
    // Keep a full configured Curve90 radius plus the minimum tangent straight
    // between the incoming corner and the first junction.
    const spanIn = Math.max(
      1,
      Math.min(
        SPUR_JUNCTION_SPAN,
        legLength - BASE_CURVE_RADIUS - MIN_SEGMENT_LENGTH
      )
    );
    const j1 = { x: stop.x - dirIn.x * spanIn, y: stop.y - dirIn.y * spanIn };
    const j2 = { x: stop.x + dirIn.x * SPUR_JUNCTION_SPAN, y: stop.y + dirIn.y * SPUR_JUNCTION_SPAN };

    if (!addManhattanEdge(ctx, a, aBox, j1, pointBox(j1), equipmentBoxes)) {
      return emptyResult('Route rejected: the configured Curve90 radius does not fit.');
    }

    const branchIn = addSpurJunction(ctx, j1, stop, perp, half);
    const branchOut = addSpurJunction(ctx, stop, j2, perp, half);

    // Project both branch tips onto one shared dock lane so the U-turn is a
    // true curve180 (semicircle), not a pair of 90° elbows.
    const alongIn = (branchIn.x - stop.x) * dirIn.x + (branchIn.y - stop.y) * dirIn.y;
    const alongOut = (branchOut.x - stop.x) * dirIn.x + (branchOut.y - stop.y) * dirIn.y;
    const acrossIn = (branchIn.x - stop.x) * perp.x + (branchIn.y - stop.y) * perp.y;
    const acrossOut = (branchOut.x - stop.x) * perp.x + (branchOut.y - stop.y) * perp.y;
    const acrossSign = acrossIn + acrossOut >= 0 ? 1 : -1;
    // Snap both tips to the same across coordinate, then push the lane out.
    const acrossTip = (acrossIn + acrossOut) / 2;

    const stopEl = existingElements.find((e) => order[nextIdx - 1].ids.includes(e.id));
    let dockDepth = SPUR_DOCK_DEPTH;
    if (stopEl && stopEl.type === 'stocker') {
      const tray = getStockerTrayPosition(stopEl);
      const dx = tray.x - stop.x;
      const dy = tray.y - stop.y;
      const trayDist = dx * perp.x + dy * perp.y;
      dockDepth = Math.max(MIN_SEGMENT_LENGTH, Math.abs(trayDist) - half);
    }
    const acrossLane = acrossTip + acrossSign * dockDepth;

    const at = (along: number, across: number): Pt => ({
      x: stop.x + dirIn.x * along + perp.x * across,
      y: stop.y + dirIn.y * along + perp.y * across,
    });

    // Snap junction branch ports onto the shared tip line (pure along-axis),
    // then run pure-perp straights out to the dock lane, closed by curve180.
    const tipIn = at(alongIn, acrossTip);
    const tipOut = at(alongOut, acrossTip);
    const dockIn = at(alongIn, acrossLane);
    const dockOut = at(alongOut, acrossLane);
    const radius = Math.max(1, Math.abs(alongOut - alongIn) / 2);

    // Close any tiny tip snap, then pure-perp legs into an *outward* curve180.
    // Bulge direction = from the stop (through-line) toward the dock mid —
    // never back toward the junctions.
    const dockMid = {
      x: (dockIn.x + dockOut.x) / 2,
      y: (dockIn.y + dockOut.y) / 2,
    };
    addStraightRail(ctx, branchIn, tipIn);
    addStraightRail(ctx, tipIn, dockIn);
    addCurve180(ctx, dockIn, dockOut, radius, {
      x: dockMid.x - stop.x,
      y: dockMid.y - stop.y,
    });
    addStraightRail(ctx, dockOut, tipOut);
    addStraightRail(ctx, tipOut, branchOut);

    // The main line now continues onward from j2, not from the stop itself.
    nodePoints[nextIdx] = j2;
    nodeBoxes[nextIdx] = pointBox(j2);
  }

  return {
    elements: ctx.elements,
    summary: {
      railCount: ctx.railN,
      curveCount: ctx.curveN,
      junctionCount: ctx.junctionN,
      totalLengthWorldUnits: Math.round(ctx.totalLength * 100) / 100,
      skippedStockerIds,
    },
  };
}
